"use strict";
var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
Object.defineProperty(exports, "__esModule", { value: true });
const axios_1 = require("axios");
const constants_1 = require("./constants");
exports.componentNames = constants_1.componentNames;
exports.customPropertyKeys = constants_1.customPropertyKeys;
exports.logLevels = constants_1.logLevels;
exports.piiTags = constants_1.piiTags;
const utils_1 = require("./utils");
exports.tagPII = utils_1.tagPII;
exports.errorToObject = utils_1.errorToObject;
let config;
let sendActionLogsTimeout;
let twilioActionLogs = [];
/**
 * Helper function used for internal tracing purposes.
 * It's enabled by a configuration property 'internalLoggerTrace'
 * @param message log message
 * @param args n number of additional arguments to log
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
const _traceLog = (message, ...args) => {
    if (config.internalLoggerTrace) {
        console.log(message, JSON.stringify(args));
    }
};
/**
 * Initializes the logger
 * @param configuration User provided configuration
 */
const init = (configuration) => {
    config = utils_1.createConfig(configuration);
    _traceLog("initialized logger with configuration", { fromUser: configuration, generated: config });
    // don't await the promise here to keep the init logic synchronous
    // note that the log override might not be fetched in time for the first few logs
    if (config.minLogLevelOverrideUrl) {
        _traceLog("fetching log level override");
        utils_1.fetchLogLevelOverride(config.minLogLevelOverrideUrl, config.accountSid).then(override => {
            _traceLog("fetched log level override", override);
            if (override) {
                config.logSinks.forEach(sink => {
                    _traceLog("applying override to log sink", sink.name);
                    sink.logLevel = override.toLowerCase();
                });
            }
        });
    }
};
exports.init = init;
/**
 * Helper to send a batch of logs and clear the accumulated logs
 */
const _batchSend = () => {
    const logs = [...twilioActionLogs];
    twilioActionLogs = [];
    _traceLog("batching logs", logs);
    return _sendLogs(logs);
};
/**
 * Batch logs and delay logging for 1 second or until 10 or more logs accumulate
 * @param log Log data
 */
const _debounceLogs = (log) => {
    twilioActionLogs.push(log);
    _traceLog("debouncing logs", log);
    clearTimeout(sendActionLogsTimeout);
    if (twilioActionLogs.length >= 10) {
        _traceLog("debouncing logs, found more than 10 logs, sending", log);
        _batchSend();
        return;
    }
    sendActionLogsTimeout = setTimeout(() => {
        _traceLog("debouncing logs, sending logs after a second", log);
        _batchSend();
    }, 1000);
};
/**
 * Helper to extract the call sid from the flex manager
 * redux store, if it exists
 */
const _getCallSidFromFlex = () => {
    if (!config.twilioFlexManager) {
        return null;
    }
    try {
        const activeConnections = config.twilioFlexManager.store.getState().flex.phone.connections;
        return activeConnections.length > 0 ? activeConnections[0].source.parameters.CallSid : null;
    }
    catch (error) {
        if (config.logErrorsToConsole) {
            console.error(utils_1.errorToObject(error));
        }
        return null;
    }
};
/**
 * Attaches a logger to all Twilio Flex actions
 * @param flex instance from Twilio
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
const registerTwilioActions = (flex) => {
    _traceLog("registering twilio actions", flex);
    if (!config.accountSid) {
        if (config.logErrorsToConsole) {
            console.warn("Twilio Account Sid was not configured, Twilio Actions logging will not be registered.");
        }
        return;
    }
    Array.from(flex.Actions.actions.keys())
        .filter(actionName => config.excludedFlexActionNames.indexOf(actionName) < 0)
        .forEach(actionName => {
        _traceLog("registering after handler for action", actionName);
        flex.Actions.addListener(`after${actionName}`, (payload) => {
            try {
                const currentCallSid = _getCallSidFromFlex();
                const log = _createLog(constants_1.twilioActionLogLevels[actionName] || constants_1.logLevels.INFO, `Twilio Flex: Executed ${actionName} action`, payload, Object.assign({ actionName, [constants_1.customPropertyKeys.WORKER_SID]: config.workerSid }, (currentCallSid ? { [constants_1.customPropertyKeys.CALL_SID]: currentCallSid } : {})), null, null, null, true);
                _traceLog("created log for action", actionName, log);
                _debounceLogs(log);
            }
            catch (error) {
                if (config.logErrorsToConsole) {
                    console.error(utils_1.errorToObject(error));
                }
            }
        });
    });
};
exports.registerTwilioActions = registerTwilioActions;
/**
 * Sends logs to all registered log sinks
 * @param logs Logs to send
 */
const _sendLogs = (logs) => __awaiter(void 0, void 0, void 0, function* () {
    _traceLog("sending logs", logs);
    const logPromises = config.logSinks.map(sink => {
        try {
            _traceLog(`sending logs to ${sink.name} log sink`, sink);
            const logsToSend = logs.filter(l => constants_1.logSeverity[l.level] >= constants_1.logSeverity[sink.logLevel]);
            _traceLog(`found ${logsToSend.length} logs for ${sink.name} log sink`, logsToSend);
            if (logsToSend.length === 0) {
                return Promise.resolve();
            }
            const logsWithoutPII = logsToSend.filter(x => !x.containsPII);
            _traceLog(`found ${logsWithoutPII.length} logs without pii for ${sink.name} log sink with pii safety set to ${sink.piiSafe}`, logsToSend);
            if (!sink.piiSafe && logsWithoutPII.length === 0) {
                return Promise.resolve();
            }
            const logPromise = sink.piiSafe ? sink.log(logsToSend) : sink.log(logsWithoutPII);
            return logPromise.catch(e => Promise.reject(new Error(e)));
        }
        catch (error) {
            const serializedError = utils_1.errorToObject(error);
            _traceLog(`error in ${sink.name} log sink`, serializedError);
            return Promise.reject(error);
        }
    });
    _traceLog(`awaiting log sink promises`);
    const results = yield Promise.all(logPromises.map(p => p.catch(e => e)));
    _traceLog(`log sink promises resolved with`, results);
    if (config.logErrorsToConsole) {
        results.filter(r => r instanceof Error).map(console.warn);
    }
});
/**
 * Helper used to create a log object from the provided parameters
 * @param level Log level
 * @param message Log message
 * @param additionalData Additional data to include in the log
 * @param customProperties Data to be extracted as top-level Application Insights properties
 * @param componentName Can be used to categorize the log
 * @param logCode Another way of categorizing the log
 * @param correlationToken Used to correlate multiple logs together
 */
const _createLog = (level, message, additionalData, customProperties, componentName, logCode, correlationToken, containsPII) => {
    _traceLog("creating log with data", {
        level,
        message,
        additionalData,
        customProperties,
        componentName,
        logCode,
        correlationToken,
        containsPII,
    });
    const currentCallSid = _getCallSidFromFlex();
    const log = {
        accountSid: config.accountSid,
        additionalData: utils_1.customStringify(additionalData, true),
        componentName: componentName || "",
        correlationToken: correlationToken || config.correlationToken,
        eventDate: new Date().toISOString(),
        level,
        logCode: logCode || 0,
        message,
        properties: Object.assign(Object.assign(Object.assign({}, (config.workerSid ? { [constants_1.customPropertyKeys.WORKER_SID]: config.workerSid } : {})), (currentCallSid ? { [constants_1.customPropertyKeys.CALL_SID]: currentCallSid } : {})), (customProperties || {})),
        source: config.source,
        loggerVersion: version,
        containsPII: !!containsPII,
    };
    _traceLog("created log with data", log);
    return log;
};
/**
 * Helper to construct a logging function with the log level pre-applied
 * @param level Log level
 */
const _createLoggerOfType = (level) => (message, additionalData, customProperties, componentName, logCode, correlationToken, containsPII) => __awaiter(void 0, void 0, void 0, function* () {
    try {
        if (!message) {
            _traceLog("log request contains no message");
            return;
        }
        yield _sendLogs([
            _createLog(level, message, additionalData, customProperties, componentName, logCode, correlationToken, containsPII),
        ]);
    }
    catch (error) {
        if (config.logErrorsToConsole) {
            console.error(utils_1.errorToObject(error));
        }
    }
});
/**
 * Logging functions for each supported level
 */
const verbose = _createLoggerOfType(constants_1.logLevels.VERBOSE);
exports.verbose = verbose;
const info = _createLoggerOfType(constants_1.logLevels.INFO);
exports.info = info;
const warn = _createLoggerOfType(constants_1.logLevels.WARN);
exports.warn = warn;
const error = _createLoggerOfType(constants_1.logLevels.ERROR);
exports.error = error;
const critical = _createLoggerOfType(constants_1.logLevels.CRITICAL);
exports.critical = critical;
/**
 * Send Twilio task router event data
 * @param formData event data as application/x-www-form-urlencoded
 */
const sendTwilioTaskRouterEvent = (formData) => __awaiter(void 0, void 0, void 0, function* () {
    _traceLog("sending twilio task router data", formData);
    return yield utils_1.sendTwilioEventFormData(config.twilioEventHooks, constants_1.twilioEventTypes.TWILIO_TASK_ROUTER, formData, config.logErrorsToConsole);
});
exports.sendTwilioTaskRouterEvent = sendTwilioTaskRouterEvent;
/**
 * Send Twilio debugger event data
 * @param formData event data as application/x-www-form-urlencoded
 */
const sendTwilioDebuggerEvent = (formData) => __awaiter(void 0, void 0, void 0, function* () {
    _traceLog("sending twilio debugger data", formData);
    return yield utils_1.sendTwilioEventFormData(config.twilioEventHooks, constants_1.twilioEventTypes.TWILIO_DEBUGGER, formData, config.logErrorsToConsole);
});
exports.sendTwilioDebuggerEvent = sendTwilioDebuggerEvent;
/**
 * Send Twilio call status event data
 * @param formData event data as application/x-www-form-urlencoded
 */
const sendTwilioCallStatusEvent = (formData) => __awaiter(void 0, void 0, void 0, function* () {
    _traceLog("sending twilio call status data", formData);
    return yield utils_1.sendTwilioEventFormData(config.twilioEventHooks, constants_1.twilioEventTypes.TWILIO_CALL_STATUS, formData, config.logErrorsToConsole);
});
exports.sendTwilioCallStatusEvent = sendTwilioCallStatusEvent;
/**
 * Posts log data to a configured url
 * @param url Url to post logs
 */
const remoteLogger = (url) => (logs) => __awaiter(void 0, void 0, void 0, function* () {
    _traceLog("sending logs to remote", { url, logs });
    return axios_1.default.post(url, logs);
});
exports.remoteLogger = remoteLogger;
/**
 * Outputs log data to the console
 * @param logs Logs to output
 */
const consoleLogger = (logs) => __awaiter(void 0, void 0, void 0, function* () {
    _traceLog("sending logs to console", logs);
    logs.forEach(data => {
        const logMessage = [`${data.level}: ${data.message}`, JSON.stringify(data)];
        _traceLog("sending log to console", { logMessage });
        if (data.level === constants_1.logLevels.CRITICAL || data.level === constants_1.logLevels.ERROR) {
            console.error(...logMessage);
        }
        else if (data.level === constants_1.logLevels.WARN) {
            console.warn(...logMessage);
        }
        else {
            console.log(...logMessage);
        }
    });
    return Promise.resolve();
});
exports.consoleLogger = consoleLogger;
// this value needs to be replaced with the actual version in the release pipeline
const version = "2.1.0";
exports.version = version;
//# sourceMappingURL=logger.js.map
